import { useCallback, useEffect, useRef, useState } from 'react';
import {
  closeChatSession,
  fetchChatSession,
  postChatAction,
  postChatMessage,
  startChatSession,
} from './api';
import {
  ANONYMOUS_ID_KEY,
  SESSION_IDLE_MS,
  SESSION_STORAGE_KEY,
} from './constants';
import type { ChatMessage, ChatSession } from './types';
import { trackChatbotEvent } from './analytics';

function getOrCreateAnonymousId(): string {
  const existing = sessionStorage.getItem(ANONYMOUS_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `anon-${Date.now()}`;
  sessionStorage.setItem(ANONYMOUS_ID_KEY, id);
  return id;
}

function loadStoredSessionId(): string | null {
  const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      sessionId: string;
      lastActivity: number;
    };
    if (Date.now() - parsed.lastActivity > SESSION_IDLE_MS) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsed.sessionId;
  } catch {
    return null;
  }
}

function persistSession(sessionId: string) {
  sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify({ sessionId, lastActivity: Date.now() })
  );
}

export function useWebsiteChatbot() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const handleCloseRef = useRef<(fromIdle?: boolean) => Promise<void>>(
    async () => {}
  );

  const touchActivity = useCallback(() => {
    if (!session?.id) return;
    persistSession(session.id);
  }, [session?.id]);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
    }
    if (!open || !session?.id) return;
    idleTimerRef.current = window.setTimeout(() => {
      void handleCloseRef.current(true);
    }, SESSION_IDLE_MS);
  }, [open, session?.id]);

  const bootstrapSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const anonymous_id = getOrCreateAnonymousId();
      const storedId = loadStoredSessionId();
      let nextSession: ChatSession;
      if (storedId) {
        try {
          nextSession = await fetchChatSession(storedId);
        } catch {
          nextSession = await startChatSession({ anonymous_id });
        }
      } else {
        nextSession = await startChatSession({
          anonymous_id,
          session_id: undefined,
        });
      }
      setSession(nextSession);
      persistSession(nextSession.id);
    } catch (err) {
      setError('Unable to start chat. Please try again.');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const appendMessages = useCallback(
    (messages: ChatMessage[]) => {
      setSession(prev =>
        prev
          ? {
              ...prev,
              messages: [...prev.messages, ...messages],
            }
          : prev
      );
      touchActivity();
    },
    [touchActivity]
  );

  const sendQuickAction = useCallback(
    async (quick_action_id: string) => {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        trackChatbotEvent('chatbot_quick_action', {
          chatbot_session_id: session.id,
          quick_action_id,
        });
        const result = await postChatMessage(session.id, { quick_action_id });
        appendMessages(result.messages);
      } catch (err) {
        setError('Something went wrong. Please try again.');
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [appendMessages, session]
  );

  const sendText = useCallback(
    async (text: string) => {
      if (!session || !text.trim()) return;
      setLoading(true);
      setError(null);
      const optimistic: ChatMessage = {
        id: `local-${Date.now()}`,
        role: 'user',
        content: text.trim(),
        message_type: 'text',
        payload: {},
      };
      setSession(prev =>
        prev ? { ...prev, messages: [...prev.messages, optimistic] } : prev
      );
      try {
        trackChatbotEvent('chatbot_user_message', {
          chatbot_session_id: session.id,
        });
        const result = await postChatMessage(session.id, { text: text.trim() });
        appendMessages(result.messages);
      } catch (err) {
        setError('Something went wrong. Please try again.');
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [appendMessages, session]
  );

  const sendAction = useCallback(
    async (action: string, data?: Record<string, unknown>) => {
      if (!session) return;
      setLoading(true);
      try {
        const result = await postChatAction(session.id, { action, data });
        appendMessages(result.messages);
      } finally {
        setLoading(false);
      }
    },
    [appendMessages, session]
  );

  const handleOpen = useCallback(async () => {
    setOpen(true);
    trackChatbotEvent('chatbot_open');
    if (!session) {
      await bootstrapSession();
    }
  }, [bootstrapSession, session]);

  const handleClose = useCallback(
    async (fromIdle = false) => {
      if (session?.id) {
        try {
          const fresh = await closeChatSession(session.id);
          setSession(fresh);
          persistSession(fresh.id);
        } catch {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          setSession(null);
        }
      }
      setOpen(false);
      if (fromIdle) {
        trackChatbotEvent('chatbot_idle_reset', {
          chatbot_session_id: session?.id,
        });
      }
    },
    [session]
  );

  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  useEffect(() => {
    if (open) {
      resetIdleTimer();
    }
    return () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, [open, resetIdleTimer, session?.messages.length]);

  return {
    open,
    loading,
    session,
    error,
    handleOpen,
    handleClose: () => handleClose(false),
    sendQuickAction,
    sendText,
    sendAction,
    setSession,
    touchActivity,
  };
}
